// Note: cribbing nearly all of this from DeviasKit 3.1 src/views/order/OrderDetailsView/index.js
// They enclose the view in a <Page /> component that does a lot of excellent stuff.
//   We should probably do much of the same, eventually

import React, { useCallback, useEffect, useState } from 'react';
import { DateTime } from 'luxon';

import { Container, Grid } from '@mui/material';

import makeStyles from '@mui/styles/makeStyles';

import axios from '../../utils/axios';
import useIsMountedRef from '../../hooks/useIsMountedRef';

import DA from '../../store/ReduxAccess';

import ControlPanel from './ControlPanel';
import ReportPreview from './ReportPreview';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    minHeight: '100%',
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
  },
}));

const replaceDateStringsWithDates = (userStats) => {
  // userStats is { personId, activityGroups: [{ type, activities: [{ date, otherPartyTemp},...] }, ...] } where 'date' is an ISO 8601 string
  const { personId, activityGroups } = userStats;
  const resolvedActivityGroups = activityGroups.map((group) => resolveDatesInActivityGroup(group));
  return { personId, activityGroups: resolvedActivityGroups };
};

const resolveDatesInActivityGroup = (group) => {
  // group is { type, activities: [{ date, otherPartyTemp},...] }
  const { type, activities } = group;
  const resolvedActivities = activities.map((activity) => resolveDateInActivity(activity));
  return { type, activities: resolvedActivities };
};

const resolveDateInActivity = (activity) => {
  // activity is { date, otherPartyTemp } where 'date' is an ISO 8601 string
  const resolvedDate = DateTime.fromISO(activity.date).toLocal();
  return { ...activity, date: resolvedDate };
};

let matrixValues = {};

const setMatrixValues = (vals) => {
  matrixValues = vals;
};

const activityTypes = ['Phone', 'Email', 'Voice Mail', 'Meeting', 'Tour', 'Text', 'Note'];

const ActivityReportPage = () => {
  const classes = useStyles();
  const isMountedRef = useIsMountedRef();
  const [startDate, setStartDate] = useState(DateTime.local().minus({ days: 7 }).startOf('day'));
  const [lastIncludedDate, setLastIncludedDate] = useState(DateTime.local().startOf('day'));
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [allStats, setAllStats] = useState([]);

  const setStateFromActivityStats = (activityStats) => {
    activityStats = activityStats.map((userStats) => replaceDateStringsWithDates(userStats));
    setAllStats(activityStats);
    const usersList = DA.getAllFirmUsers();
    setAllUsers(usersList);
    setSelectedUsers(usersList);
  };

  const orderAndSetSelectedUsers = (someUsers) => {
    // 'someUsers is a subset of 'allUsers'
    // our goal here is to generate a list of the same users, but in the order they are in in 'allUsers'
    // then we'll call setSelectedUsers with that list
    let ordered = [];
    allUsers.forEach((knownUser) => {
      if (someUsers.indexOf(knownUser) >= 0) ordered = [...ordered, knownUser];
    });
    setSelectedUsers(ordered);
  };

  const getActivityStats = useCallback(async () => {
    try {
      const response = await axios.get('/api/firm/activityStats');

      if (isMountedRef.current) {
        const stats = response.data.activityStats;
        setStateFromActivityStats(stats);
      }
    } catch (err) {
      console.error(err);
    }
  }, [isMountedRef]);

  useEffect(() => {
    getActivityStats();
  }, [getActivityStats]);

  const download = async () => {
    try {
      const postBody = {
        firstDate: startDate.toISO(),
        lastDate: lastIncludedDate.toISO(),
        activityTypes: activityTypes,
        userNames: selectedUsers.map((user) => user.name),
        matrixValues: matrixValues,
      };

      const response = await axios.request({
        responseType: 'blob',
        url: '/api/firm/activityReport', // just reproduces the preview
        method: 'post',
        headers: {
          Accept: 'application/vnd.oasis.opendocument.spreadsheet',
        },
        data: postBody,
      });

      // when we get a response, open up an invisible link and click it
      const fileBuffer = response.data;
      const blob = new Blob([fileBuffer], { type: 'application/vnd.oasis.opendocument.spreadsheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'activityReport.ods';
      a.click();
    } catch (err) {
      console.error(err);
    }
  };

  if (allStats.length === 0) {
    return null;
  }

  return (
    <Container className={classes.root} maxWidth={false}>
      <Grid container spacing={2}>
        <Grid item md={12}>
          <ControlPanel
            startDate={startDate}
            startDateSetter={setStartDate}
            lastIncludedDate={lastIncludedDate}
            lastIncludedDateSetter={setLastIncludedDate}
            allUsers={allUsers}
            selectedUsers={selectedUsers}
            selectedUsersSetter={orderAndSetSelectedUsers}
            downloadFunc={() => download()}
          />
        </Grid>
      </Grid>
      <Grid item md={12}>
        <ReportPreview
          startDate={startDate}
          lastIncludedDate={lastIncludedDate}
          allUsers={allUsers}
          selectedUsers={selectedUsers}
          allStats={allStats}
          activityTypes={activityTypes}
          matrixValuesSetter={setMatrixValues}
        />
      </Grid>
    </Container>
  );
};

export default ActivityReportPage;
